import identity from 'lodash/identity';
import isEmpty from 'lodash/isEmpty';
import pickBy from 'lodash/pickBy';
import { createSelector } from 'reselect';

import {
  ADD_EMAIL_TEMPLATE_VALIDATION_SCHEMA,
  ADD_NOTE_TEMPLATE_VALIDATION_SCHEMA,
  INITIAL_TEMPLATE_BUILDER_VALUES,
  NO_TEMPLATE,
} from '../../constants/templates';
import {
  ClaimLinkEditorPageEnum,
  EmailTemplateTypesEnum,
  TCollectiveTemplateItem,
  TemplateTypesEnum,
} from '../../types/templates';
import { filterClaimLinkTemplatesForUser, getAddTemplateInitialValue } from '../../utils/templates';
import { IAppState } from '../reducers';
import { selectAdminType, selectUserDepartmentId } from './auth';

export const selectRootState = (state: IAppState) => state.templates;

export const selectNotes = createSelector(selectRootState, (templates) =>
  templates.notes ? templates.notes : undefined,
);

export const selectEmails = createSelector(selectRootState, (templates) =>
  templates.emails ? templates.emails : undefined,
);

export const selectClaimLinks = createSelector(selectRootState, (templates) =>
  templates.claimLinks ? templates.claimLinks : undefined,
);

export const selectNoteTemplates = createSelector(selectNotes, (notes) => (notes ? notes.templates : undefined));

export const selectEmailTemplates = createSelector(selectEmails, (emails) => (emails ? emails.templates : undefined));

export const selectClaimLinkTemplates = createSelector(selectClaimLinks, (claimLinks) =>
  claimLinks ? claimLinks.templates : undefined,
);

export const selectIsClaimLinkTemplateSaving = createSelector(
  selectClaimLinks,
  (claimLinks) => claimLinks?.isSaving || false,
);

export const selectClaimLinkTemplate = createSelector(
  selectClaimLinks,
  (claimLinks) => claimLinks?.currentTemplate || null,
);

export const selectIsClaimLinkTemplateLoading = createSelector(
  selectClaimLinks,
  (claimLinks) => claimLinks?.isCurrentTemplateLoading || false,
);

export const selectFilteredClaimLinkTemplatesForUser = createSelector(
  selectClaimLinkTemplates,
  selectUserDepartmentId,
  selectAdminType,
  (templates, userDepartmentId, adminType) => {
    if (!templates) {
      return undefined;
    }

    return filterClaimLinkTemplatesForUser(templates, userDepartmentId, adminType);
  },
);

export const selectClaimLinkTemplatesIsLoading = createSelector(selectClaimLinks, (claimLinks) =>
  claimLinks ? claimLinks.isLoading : false,
);

export const selectClaimLinkTemplatesTotalCount = createSelector(selectClaimLinks, (claimLinks) =>
  claimLinks ? claimLinks.totalCount : 0,
);

export const selectClaimLinkLastFetchParams = createSelector(selectClaimLinks, (claimLinks) =>
  claimLinks ? claimLinks.lastFetchParams : null,
);

export const selectAllTemplates = createSelector(
  selectEmailTemplates,
  selectNoteTemplates,
  (emailTemplates, noteTemplates) => {
    const preparedList = <T extends { item_id: string }>(list: T[], templateType: TemplateTypesEnum) =>
      list
        .filter((template) => template.item_id !== NO_TEMPLATE)
        .map((template) => ({
          ...template,
          templateType,
        }));

    const templateList: TCollectiveTemplateItem[] = [
      ...(noteTemplates ? preparedList(noteTemplates, TemplateTypesEnum.Note) : []),
      ...(emailTemplates ? preparedList(emailTemplates, TemplateTypesEnum.Email) : []),
    ];

    return templateList;
  },
);

export const selectNoteTemplatesIsLoading = createSelector(selectNotes, (notes) => (notes ? notes.isLoading : false));
export const selectEmailTemplatesIsLoading = createSelector(selectEmails, (emails) =>
  emails ? emails.isLoading : false,
);

export const selectNoteTemplatesIsLoaded = createSelector(
  selectNotes,
  selectNoteTemplatesIsLoading,
  (notes, isLoading) => notes && notes.templates !== null && !isLoading,
);

export const selectEmailTemplatesIsLoaded = createSelector(
  selectEmails,
  selectEmailTemplatesIsLoading,
  (emails, isLoading) => emails && emails.templates !== null && !isLoading,
);

export const selectNoteTemplatesNeverLoaded = createSelector(
  selectNotes,
  (notes) => !notes || notes.templates === null,
);

export const selectEmailTemplatesNeverLoaded = createSelector(
  selectEmails,
  (emails) => !emails || emails.templates === null,
);

export const selectTemplatesIsLoaded = createSelector(
  selectNoteTemplatesIsLoaded,
  selectEmailTemplatesIsLoaded,
  (isNoteLoaded, isEmailLoaded) => isNoteLoaded && isEmailLoaded,
);

export const selectEmailDelayedShippingTemplates = createSelector(selectEmailTemplates, (templates) =>
  templates ? templates.filter((template) => template.type === EmailTemplateTypesEnum.DELAYED_SHIPPING) : undefined,
);

export const selectEmailDigitalGiftTemplates = createSelector(selectEmailTemplates, (templates) =>
  templates ? templates.filter((template) => template.type === EmailTemplateTypesEnum.DIGITAL_GIFT) : undefined,
);

export const selectTemplateDetails = createSelector(selectRootState, (templates) => templates.templateDetails);

export const selectTemplateDetailsTemplate = createSelector(selectTemplateDetails, (templateDetails) =>
  templateDetails ? templateDetails.template : undefined,
);

export const selectTemplateIsLoading = createSelector(selectTemplateDetails, (templateDetails) =>
  templateDetails ? templateDetails.isLoading : false,
);

export const selectTemplateValidationSchema = createSelector(selectTemplateDetailsTemplate, (template) => {
  return template && template.templateType === TemplateTypesEnum.Email
    ? ADD_EMAIL_TEMPLATE_VALIDATION_SCHEMA
    : ADD_NOTE_TEMPLATE_VALIDATION_SCHEMA;
});

export const selectTemplateInitialValues = createSelector(
  selectTemplateDetailsTemplate,
  selectAdminType,
  selectUserDepartmentId,
  (templateDetails, adminType, userDepartmentId) => {
    const { templateType = TemplateTypesEnum.Note, ...restObj } = templateDetails || {};

    if (!isEmpty(pickBy(restObj, identity))) {
      return templateDetails!;
    }

    return getAddTemplateInitialValue(templateType, adminType, userDepartmentId);
  },
);

const createSelectTemplateById = (itemId: string | undefined) =>
  createSelector(selectNoteTemplates, selectEmailTemplates, (noteTemplates, emailTemplates) => {
    if (!itemId) {
      return null;
    }

    return [
      ...(noteTemplates
        ? noteTemplates.map((template) => ({ ...template, templateType: TemplateTypesEnum.Note }))
        : []),
      ...(emailTemplates
        ? emailTemplates.map((template) => ({ ...template, templateType: TemplateTypesEnum.Email }))
        : []),
    ]?.find((template) => template.item_id === itemId);
  });

type SelectTemplateByIdResult = ReturnType<typeof createSelectTemplateById>;
const selectTemplateByIdCache = new Map<string | undefined, SelectTemplateByIdResult>();

export const selectTemplateById = (itemId: string | undefined): SelectTemplateByIdResult => {
  if (!selectTemplateByIdCache.has(itemId)) {
    selectTemplateByIdCache.set(itemId, createSelectTemplateById(itemId));
  }
  return selectTemplateByIdCache.get(itemId)!;
};

export const selectClaimLinkTemplateById = (uid: string | undefined) =>
  createSelector(selectClaimLinkTemplates, (templates) => (uid ? templates?.find((t) => t.uid === uid) ?? null : null));

export const selectClaimLinkBuilderDraft = createSelector(
  selectClaimLinks,
  (claimLinks) => claimLinks?.draft || INITIAL_TEMPLATE_BUILDER_VALUES,
);

export const selectIsTemplateDraftDirty = createSelector(selectClaimLinks, (claimLinks) => {
  const { draft, draftSnapshot } = claimLinks || {};
  if (!draft || !draftSnapshot) {
    return false;
  }
  const { activePageId: _activePageId, ...rest } = draft;
  return JSON.stringify(rest) !== draftSnapshot;
});

export const selectTemplateValidationErrors = createSelector(
  selectClaimLinks,
  (claimLinks) => claimLinks?.validationErrors || null,
);

export const selectTemplateValidationModal = createSelector(
  selectClaimLinks,
  (claimLinks) => claimLinks?.validationModal,
);

export const selectPageHistory = createSelector(selectClaimLinks, (claimLinks) => claimLinks?.pageHistory);

const selectCanUndoMap = Object.fromEntries(
  Object.values(ClaimLinkEditorPageEnum).map((pageId) => [
    pageId,
    createSelector(selectPageHistory, (pageHistory) => (pageHistory?.[pageId]?.past.length ?? 0) > 0),
  ]),
) as Record<ClaimLinkEditorPageEnum, ReturnType<typeof createSelector>>;

export const selectCanUndo = (pageId: ClaimLinkEditorPageEnum) => selectCanUndoMap[pageId];

const selectCanRedoMap = Object.fromEntries(
  Object.values(ClaimLinkEditorPageEnum).map((pageId) => [
    pageId,
    createSelector(selectPageHistory, (pageHistory) => (pageHistory?.[pageId]?.future.length ?? 0) > 0),
  ]),
) as Record<ClaimLinkEditorPageEnum, ReturnType<typeof createSelector>>;

export const selectCanRedo = (pageId: ClaimLinkEditorPageEnum) => selectCanRedoMap[pageId];
